import { SearchOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, Select } from 'antd';
import React, { useEffect } from 'react';

import { DATE_FORMAT_DISPLAY } from '@constants';
import { FieldTypes } from '@constants/formConfig';
import Utils from '@utils';

const { RangePicker } = DatePicker;

// Port từ ra-log-monitor-cms/source/src/containers/log/SearchFormLog.js (class BasicForm -> hook).
// Chỉ dùng cho trang /logs. KHÁC SearchForm chung ở 3 điểm — giữ nguyên:
//   1. Ô nhập: `allowClear` theo prop + `onChange` gọi thẳng `handleClear(value)` của trang
//      (trang dùng nó để xoá query string và dọn dữ liệu khi người dùng xoá trắng ô)
//   2. Nút thứ hai "Làm mới" KHÔNG reset form — nó gọi `refreshTable()`
//   3. Giữa icon và chữ "Làm mới" KHÔNG có dấu cách (khác SearchForm chung)
// componentDidMount CHỈ setFieldsValue, KHÔNG tự submit (khác LogSearchForm của license-cms).
const SearchFormLog = ({
    searchFields,
    className,
    hiddenAction,
    initialValues,
    allowClear,
    onSubmit,
    handleClear,
    refreshTable,
}) => {
    const [form] = Form.useForm();

    const getPlaceHolder = (item) => item.seachPlaceholder || `Tìm kiếm bằng ${Utils.camelCaseToTitleCase(item.key)}`;

    const getLabelFieldItem = (item) => (item.isShowFilterLabel ? item.title : '');

    const handleSubmit = (formValues) => onSubmit(formValues);

    const getSelectOptions = (fieldItem) => {
        let { options, initialValue, optionValueKey, optionLabelKey } = fieldItem;
        optionValueKey = optionValueKey || 'value';
        optionLabelKey = optionLabelKey || 'label';
        if (options && options.length > 0) return options;
        else if (initialValue) return [{ [optionValueKey]: initialValue, [optionLabelKey]: '' }];
        else return [];
    };

    const onChangeDateField = (date, fieldItem) => {
        if (fieldItem.onChange) fieldItem.onChange(date);
        if (fieldItem.fieldNameChild) form.setFieldValue(fieldItem.fieldNameChild, null);
    };

    const onFilterOption = (input, option) =>
        Utils.removeAccents(String(option.children).toLowerCase()).indexOf(Utils.removeAccents(input.toLowerCase())) >=
        0;

    const renderFormType = (fieldItem) => {
        if (fieldItem === undefined || fieldItem === null) return null;

        if (fieldItem.fieldType === FieldTypes.DATE) {
            const dateFormat = fieldItem.format || DATE_FORMAT_DISPLAY;
            return (
                <DatePicker
                    style={{ width: fieldItem.width || 200 }}
                    placeholder={getPlaceHolder(fieldItem)}
                    defaultValue={fieldItem.initialValue}
                    format={dateFormat}
                    onChange={(date) => onChangeDateField(date, fieldItem)}
                    disabledDate={fieldItem.disabledDate}
                />
            );
        } else if (fieldItem.fieldType === FieldTypes.DATE_RANGE) {
            const dateFormat = fieldItem.format || DATE_FORMAT_DISPLAY;
            return (
                <RangePicker
                    placeholder={getPlaceHolder(fieldItem)}
                    disabledDate={fieldItem.disabledDate}
                    style={{ width: fieldItem.width || 200 }}
                    format={dateFormat}
                    onChange={(date) => onChangeDateField(date, fieldItem)}
                    allowClear={allowClear}
                />
            );
        } else if (fieldItem.fieldType === FieldTypes.SELECT) {
            let { optionValueKey, optionLabelKey, loading, allowClear: fieldAllowClear } = fieldItem;
            optionValueKey = optionValueKey || 'value';
            optionLabelKey = optionLabelKey || 'label';
            return (
                <Select
                    showArrow
                    allowClear={fieldAllowClear === false ? false : true}
                    loading={loading}
                    placeholder={getPlaceHolder(fieldItem)}
                    style={{ width: fieldItem.width || 200 }}
                    defaultValue={fieldItem.initialValue}
                >
                    {getSelectOptions(fieldItem).map((option) => (
                        <Select.Option key={option[optionValueKey]}>{option[optionLabelKey]}</Select.Option>
                    ))}
                </Select>
            );
        } else if (fieldItem.fieldType === FieldTypes.AUTOCOMPLETE) {
            let {
                options,
                onSearch,
                optionValueKey,
                optionLabelKey,
                optionLabelProp,
                renderItem,
                loading,
                allowClear: fieldAllowClear,
            } = fieldItem;
            optionValueKey = optionValueKey || 'id';
            optionLabelKey = optionLabelKey || 'name';
            optionLabelProp = optionLabelProp || 'children';
            return (
                <Select
                    showSearch
                    showArrow={loading || false}
                    allowClear={fieldAllowClear === false ? false : true}
                    loading={loading}
                    style={{ minWidth: 200 }}
                    placeholder={getPlaceHolder(fieldItem)}
                    defaultActiveFirstOption={false}
                    onSearch={onSearch}
                    filterOption={onSearch ? false : onFilterOption}
                    optionLabelProp={optionLabelProp}
                >
                    {options.map((option) =>
                        renderItem ? (
                            renderItem(option[optionValueKey], option[optionValueKey], option)
                        ) : (
                            <Select.Option key={option[optionValueKey]}>{option[optionLabelKey]}</Select.Option>
                        ),
                    )}
                </Select>
            );
        }

        return (
            <Input
                autoComplete="off"
                allowClear={allowClear}
                onChange={(e) => handleClear && handleClear(e.target.value)}
                style={{ width: fieldItem.width || 200 }}
                placeholder={getPlaceHolder(fieldItem)}
            />
        );
    };

    // nguồn componentDidMount: CHỈ đổ initialValues, KHÔNG tự submit
    useEffect(() => {
        form.setFieldsValue(initialValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // nguồn componentDidUpdate: field isSelectFirstOption chưa có giá trị -> chọn option đầu
    useEffect(() => {
        searchFields.forEach((searchField) => {
            if (
                searchField.isSelectFirstOption &&
                searchField.options &&
                searchField.options.length > 0 &&
                !form.getFieldValue(searchField.key)
            ) {
                const optionValueKey = searchField.optionValueKey || 'id';
                form.setFieldValue(searchField.key, searchField.options[0][optionValueKey].toString());
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchFields]);

    return (
        <Form form={form} layout="inline" onFinish={handleSubmit} className={className || 'search-form'}>
            {searchFields.map((fieldItem) => (
                <Form.Item key={fieldItem.key} label={getLabelFieldItem(fieldItem)} name={fieldItem.key}>
                    {renderFormType(fieldItem)}
                </Form.Item>
            ))}
            {hiddenAction ? null : (
                <Form.Item style={{ marginTop: '3.5px' }}>
                    <Button icon={<SearchOutlined />} type="primary" htmlType="submit">
                        Tìm kiếm
                    </Button>
                    {/* nguồn: nút này KHÔNG reset form, nó gọi refreshTable(); icon dính liền chữ */}
                    <Button style={{ marginLeft: 8 }} onClick={() => refreshTable && refreshTable()}>
                        <i className="icfa fa-refresh" />
                        Làm mới
                    </Button>
                </Form.Item>
            )}
        </Form>
    );
};

export default SearchFormLog;
